// Parser / serializer CSV minimalista y robusto (RFC 4180), sin dependencias.
//
// Se usa para reprocesar en cliente el CSV que emite el backend
// (ListCollectionsController::csvResponse): mismo separador ',', campos con
// comas/comillas/saltos de línea entrecomillados y comillas dobladas ("") como
// escape — exactamente lo que produce fputcsv de PHP con sus defaults.
//
// El BOM UTF-8 inicial (\xEF\xBB\xBF que escribe el backend para Excel) se
// elimina antes de parsear y se reescribe al serializar, para que el fichero
// exportado conserve la misma cabecera y Excel siga autodetectando UTF-8.

export const UTF8_BOM = "﻿";

// Parsea un texto CSV completo a matriz de filas de strings. Respeta:
//  - campos entrecomillados con comas, comillas escapadas ("") y saltos de línea.
//  - saltos CRLF y LF.
// Nota: una última línea vacía (típica del trailing newline) se descarta.
export function parseCsv(input: string): string[][] {
  let text = input;
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1); // strip BOM

  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < n) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      endField();
      i += 1;
      continue;
    }
    if (c === "\r") {
      // CRLF o CR suelto: cierra fila y consume el \n si viene detrás.
      endRow();
      if (text[i + 1] === "\n") i += 2;
      else i += 1;
      continue;
    }
    if (c === "\n") {
      endRow();
      i += 1;
      continue;
    }
    field += c;
    i += 1;
  }

  // Vuelca la última fila si hay contenido pendiente (sin trailing newline) o si
  // se abrió una fila. Descarta una fila final totalmente vacía (["" ]) fruto del
  // salto de línea final.
  if (field.length > 0 || row.length > 0) {
    endRow();
  }
  if (
    rows.length > 0 &&
    rows[rows.length - 1].length === 1 &&
    rows[rows.length - 1][0] === ""
  ) {
    rows.pop();
  }

  return rows;
}

// Entrecomilla un campo si contiene coma, comilla, CR o LF (RFC 4180),
// doblando las comillas internas.
function escapeField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// Serializa una matriz a CSV. Usa CRLF como terminador de línea (RFC 4180, y lo
// que espera Excel) y antepone el BOM UTF-8 para conservar la cabecera del
// backend.
export function serializeCsv(rows: string[][], withBom = true): string {
  const body = rows
    .map((row) => row.map(escapeField).join(","))
    .join("\r\n");
  return (withBom ? UTF8_BOM : "") + body + (rows.length > 0 ? "\r\n" : "");
}
